import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Coins, MapPin, ShoppingBag, Truck } from "lucide-react";
import { toast } from "sonner";
import { useMe } from "@/context/MeContext";
import { useCart } from "@/context/CartContext";
import { eur, publicGet } from "@/lib/api";
import { PriceSteps } from "@/components/club/PriceSteps";
import { PointsEarn, PointsNeeded } from "@/components/club/PointsEarn";
import { Seo } from "@/components/Seo";
import { AvailabilityBadge, availabilityOf } from "@/components/club/StatusBadge";

export default function OfferDetail() {
  const { id } = useParams();
  const { me, isSignedIn } = useMe();
  const { add } = useCart();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    publicGet(`/marketplace/offers/${id}`).then(setOffer).catch(() => setError(true));
  }, [id]);

  if (error) return <p className="py-24 text-center text-slate-400">Offerta non disponibile.</p>;
  if (!offer) return <p className="py-24 text-center text-slate-500">Caricamento…</p>;
  const status = me?.status || "bronze";
  const soldOut = offer.stock <= 0;
  const freeOver = Number(offer.partner?.free_shipping_over || 0);
  const shipCost = freeOver && offer[`price_${status}`] >= freeOver ? 0 : Number(offer.partner?.shipping_cost || 0);
  const ld = {
    "@context": "https://schema.org", "@type": "Product", name: offer.name, description: (offer.description || "").slice(0, 500),
    image: offer.image_url || undefined, brand: offer.brand ? { "@type": "Brand", name: offer.brand } : undefined, gtin13: offer.ean || undefined,
    sku: offer.code || undefined,
    category: offer.subcategory || offer.category,
    offers: { "@type": "Offer", priceCurrency: "EUR", price: offer.price_bronze, url: window.location.href,
      availability: `https://schema.org/${availabilityOf(offer)[2]}`, itemCondition: "https://schema.org/NewCondition", seller: { "@type": "Organization", name: offer.partner?.company_name } },
  };

  const payWithPoints = () => {
    if (!isSignedIn) return navigate("/sign-in");
    navigate("/checkout", { state: { items: [{ offer, qty: 1 }], usePoints: true, direct: true } });
  };

  return (
    <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 lg:grid-cols-2" data-testid="offer-detail">
      <Seo title={offer.name} description={`${offer.name} a ${eur(offer.price_bronze)}${offer.brand ? ` · ${offer.brand}` : ""}. ${(offer.description || "").slice(0, 120)} Guadagna punti con Club Prezzi.`} image={offer.image_url} type="product" jsonLd={ld} />
      <img src={offer.image_url || "https://images.unsplash.com/photo-1743356174397-d6da6f014f8f?w=1200&q=80"} alt={offer.name} className={`aspect-[4/3] w-full rounded-3xl border border-white/10 ${offer.subcategory ? "bg-white object-contain p-6" : "object-cover"}`} />
      <div>
        <p className="text-xs uppercase tracking-widest text-amber-400">{offer.subcategory ? `${offer.category} · ${offer.subcategory}` : `${offer.category} · ${offer.partner?.company_name}`}</p>
        <h1 data-testid="offer-title" className="mt-2 break-words font-display text-2xl font-extrabold tracking-tight text-white sm:text-4xl">{offer.name}</h1>
        <p className="mt-4 whitespace-pre-line break-words text-slate-400">{(offer.description || "").replace(/<[^>]+>/g, " ").replace(/ {2,}/g, " ").trim()}</p>
        {offer.partner_id === "house-ingrosso" && (
          <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-slate-500" data-testid="offer-specs">
            {offer.brand && <><dt>Marca</dt><dd className="text-slate-300">{offer.brand}</dd></>}
            {offer.subcategory && <><dt>Categoria</dt><dd className="text-slate-300">{offer.subcategory}</dd></>}
            {offer.ean && <><dt>EAN</dt><dd className="text-slate-300">{offer.ean}</dd></>}
            <dt>Venduto da</dt><dd className="text-slate-300">Club Prezzi · Managebnb srls</dd>
          </dl>
        )}
        <div className="mt-8"><PriceSteps offer={offer} current={status} /></div>
        <div className="mt-3"><PointsEarn amount={offer[`price_${status}`]} multiplier={offer.partner_id === "house-ingrosso" ? 3 : 1} label={offer.partner_id === "house-ingrosso" ? "Con questo acquisto guadagni il TRIPLO" : "Con questo acquisto guadagni"} testid="offer-detail-points" /></div>
        <div className="max-w-sm"><PointsNeeded amount={offer[`price_${status}`] + shipCost} note="spedizione inclusa" testid="offer-detail-points-needed" /></div>
        <div className="mt-6 space-y-1 text-sm text-slate-400">
          <p className="flex items-center gap-2"><Truck className="h-4 w-4" /> Spedizione {shipCost > 0 ? eur(shipCost) : "gratuita"}{offer.partner?.free_shipping_over ? ` · gratis sopra ${eur(offer.partner.free_shipping_over)}` : ""}</p>
          {offer.partner?.pickup && <p className="flex items-center gap-2"><MapPin className="h-4 w-4" /> Ritiro disponibile presso l'azienda</p>}
          <p><AvailabilityBadge offer={offer} testid="offer-stock" /></p>
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <button data-testid="add-to-cart-btn" disabled={soldOut} onClick={() => { add(offer); toast.success("Aggiunto al carrello"); }}
            className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-6 py-3 font-semibold text-black transition-colors hover:bg-amber-400 disabled:opacity-50">
            <ShoppingBag className="h-4 w-4" /> Aggiungi al carrello
          </button>
          <button data-testid="pay-with-points-button" disabled={soldOut} onClick={payWithPoints}
            className="inline-flex items-center gap-2 rounded-full border border-amber-500/50 px-6 py-3 font-semibold text-amber-300 transition-colors hover:bg-amber-500/10 disabled:opacity-50">
            <Coins className="h-4 w-4" /> Paga con i punti
          </button>
        </div>
      </div>
    </div>
  );
}
